import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [createData, setCreateData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'EUR',
    sla_days: '',
    included_tasks_summary: '',
    monthly_limits: {
      requests: '',
      tasks: '',
      storage_gb: '',
    },
  });
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'EUR',
    sla_days: '',
    included_tasks_summary: '',
    monthly_limits: {
      requests: '',
      tasks: '',
      storage_gb: '',
    },
  });
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});

  const loadPackages = async () => {
    try {
      const res = await api.get('/packages');
      setPackages(res.data || []);
    } catch (error) {
      console.error('Error al cargar paquetes:', error);
      toast.error('Error al cargar paquetes');
    }
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/packages', {
        name: createData.name,
        description: createData.description,
        price: Number(createData.price),
        currency: createData.currency,
        sla_days: Number(createData.sla_days),
        included_tasks_summary: createData.included_tasks_summary,
        monthly_limits: {
          requests: Number(createData.monthly_limits.requests),
          tasks: Number(createData.monthly_limits.tasks),
          storage_gb: Number(createData.monthly_limits.storage_gb),
        },
      });
      toast.success('Paquete creado');
      setCreateOpen(false);
      setCreateData({
        name: '',
        description: '',
        price: '',
        currency: 'EUR',
        sla_days: '',
        included_tasks_summary: '',
        monthly_limits: { requests: '', tasks: '', storage_gb: '' },
      });
      loadPackages();
    } catch (error) {
      console.error('Error al crear paquete:', error);
      toast.error('Error al crear paquete');
    }
  };

  const openEdit = (pkg) => {
    setSelectedPackage(pkg);
    setEditData({
      name: pkg.name || '',
      description: pkg.description || '',
      price: pkg.price ?? '',
      currency: pkg.currency || 'EUR',
      sla_days: pkg.sla_days ?? '',
      included_tasks_summary: pkg.included_tasks_summary || '',
      monthly_limits: {
        requests: pkg.monthly_limits?.requests ?? '',
        tasks: pkg.monthly_limits?.tasks ?? '',
        storage_gb: pkg.monthly_limits?.storage_gb ?? '',
      },
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedPackage) return;
    try {
      await api.put(`/packages/${selectedPackage.id}`, {
        name: editData.name,
        description: editData.description,
        price: Number(editData.price),
        currency: editData.currency,
        sla_days: Number(editData.sla_days),
        included_tasks_summary: editData.included_tasks_summary,
        monthly_limits: {
          requests: Number(editData.monthly_limits.requests),
          tasks: Number(editData.monthly_limits.tasks),
          storage_gb: Number(editData.monthly_limits.storage_gb),
        },
      });
      toast.success('Paquete actualizado');
      setEditOpen(false);
      setSelectedPackage(null);
      loadPackages();
    } catch (error) {
      console.error('Error al actualizar paquete:', error);
      toast.error('Error al actualizar paquete');
    }
  };

  const handleDelete = async (pkg) => {
    const confirmed = window.confirm(`Eliminar paquete \"${pkg.name}\"?`);
    if (!confirmed) return;
    try {
      await api.delete(`/packages/${pkg.id}`);
      toast.success('Paquete eliminado');
      loadPackages();
    } catch (error) {
      console.error('Error al eliminar paquete:', error);
      toast.error('Error al eliminar paquete');
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return packages.filter((pkg) => {
      if (!normalized) return true;
      return pkg.name?.toLowerCase().includes(normalized);
    });
  }, [packages, query]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aValue = (a[sortKey] ?? '').toString().toLowerCase();
      const bValue = (b[sortKey] ?? '').toString().toLowerCase();
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const toggleSelect = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExport = () => {
    const rows = sorted.filter((pkg) => selected[pkg.id]);
    if (!rows.length) {
      toast.error('Selecciona al menos un paquete');
      return;
    }
    const header = ['name', 'price', 'currency', 'sla_days'];
    const csv = [
      header.join(','),
      ...rows.map((p) => [p.name, p.price, p.currency, p.sla_days].map((v) => `"${v || ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'paquetes.csv';
    link.click();
  };

  return (
    <div data-testid="packages-page">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Paquetes</h1>
          <p className="text-muted-foreground">Administra planes y limites</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>Exportar CSV</Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-package-button">
                <Plus className="w-4 h-4" />Crear Paquete
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuevo Paquete</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label>Nombre</Label>
                  <Input
                    value={createData.name}
                    onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Descripcion</Label>
                  <Textarea
                    value={createData.description}
                    onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Precio</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={createData.price}
                      onChange={(e) => setCreateData({ ...createData, price: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Moneda</Label>
                    <Input
                      value={createData.currency}
                      onChange={(e) => setCreateData({ ...createData, currency: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>SLA (dias)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={createData.sla_days}
                    onChange={(e) => setCreateData({ ...createData, sla_days: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Resumen de tareas incluidas</Label>
                  <Textarea
                    value={createData.included_tasks_summary}
                    onChange={(e) => setCreateData({ ...createData, included_tasks_summary: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Solicitudes</Label>
                    <Input
                      type="number"
                      min="0"
                      value={createData.monthly_limits.requests}
                      onChange={(e) =>
                        setCreateData({
                          ...createData,
                          monthly_limits: { ...createData.monthly_limits, requests: e.target.value },
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>Tareas</Label>
                    <Input
                      type="number"
                      min="0"
                      value={createData.monthly_limits.tasks}
                      onChange={(e) =>
                        setCreateData({
                          ...createData,
                          monthly_limits: { ...createData.monthly_limits, tasks: e.target.value },
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>Almacenamiento (GB)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={createData.monthly_limits.storage_gb}
                      onChange={(e) =>
                        setCreateData({
                          ...createData,
                          monthly_limits: { ...createData.monthly_limits, storage_gb: e.target.value },
                        })
                      }
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full">Crear</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por nombre"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={`${sortKey}:${sortDir}`}
            onChange={(e) => {
              const [key, dir] = e.target.value.split(':');
              setSortKey(key);
              setSortDir(dir);
            }}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="name:asc">Nombre A-Z</option>
            <option value="name:desc">Nombre Z-A</option>
            <option value="price:asc">Precio menor</option>
            <option value="price:desc">Precio mayor</option>
          </select>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {sorted.map((pkg) => (
          <Card key={pkg.id} className="hover:border-primary/50 hover:shadow-md transition-all">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-2xl font-heading font-bold mb-2">{pkg.name}</h3>
                  <p className="text-muted-foreground mb-4">{pkg.description}</p>
                </div>
                <input type="checkbox" checked={Boolean(selected[pkg.id])} onChange={() => toggleSelect(pkg.id)} />
              </div>
              <div className="text-3xl font-heading font-bold text-primary mb-2">
                {pkg.price} {pkg.currency}
              </div>
              <div className="text-sm text-muted-foreground mb-4">SLA: {pkg.sla_days} dias</div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(pkg)}>
                  <Pencil className="w-4 h-4 mr-1" />Editar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(pkg)}>
                  <Trash2 className="w-4 h-4 mr-1" />Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Paquete</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Descripcion</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Precio</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editData.price}
                  onChange={(e) => setEditData({ ...editData, price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Moneda</Label>
                <Input
                  value={editData.currency}
                  onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>SLA (dias)</Label>
              <Input
                type="number"
                min="1"
                value={editData.sla_days}
                onChange={(e) => setEditData({ ...editData, sla_days: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Resumen de tareas incluidas</Label>
              <Textarea
                value={editData.included_tasks_summary}
                onChange={(e) => setEditData({ ...editData, included_tasks_summary: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Solicitudes</Label>
                <Input
                  type="number"
                  min="0"
                  value={editData.monthly_limits.requests}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      monthly_limits: { ...editData.monthly_limits, requests: e.target.value },
                    })
                  }
                  required
                />
              </div>
              <div>
                <Label>Tareas</Label>
                <Input
                  type="number"
                  min="0"
                  value={editData.monthly_limits.tasks}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      monthly_limits: { ...editData.monthly_limits, tasks: e.target.value },
                    })
                  }
                  required
                />
              </div>
              <div>
                <Label>Almacenamiento (GB)</Label>
                <Input
                  type="number"
                  min="0"
                  value={editData.monthly_limits.storage_gb}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      monthly_limits: { ...editData.monthly_limits, storage_gb: e.target.value },
                    })
                  }
                  required
                />
              </div>
            </div>
            <Button type="submit" className="w-full">Guardar cambios</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default Packages;
